import { api } from "@/lib/api";
import type { AspectRatio } from "@/lib/api";

export type DirectStatus =
  | "DRAFT"
  | "ANALYZING"
  | "PLANNED"
  | "TOO_MANY_CLIPS"
  | "ANALYSIS_FAILED"
  | "GENERATING"
  | "FAILED"
  | "COMPLETED";
export type SegmentStatus = "PENDING" | "GENERATING" | "READY" | "FAILED";

export interface DirectReference {
  id: string;
  filename?: string | null;
  content_type?: string | null;
}

export interface DirectPlanClip {
  index: number;
  title: string;
  duration_target_seconds: number;
}

export interface DirectPlan {
  mode: "single_clip" | "multi_clip";
  reason?: string | null;
  estimated_total_duration_seconds: number;
  clips: DirectPlanClip[];
}

export interface DirectSegment {
  id: string;
  index: number;
  title: string;
  duration_target_seconds?: number | null;
  status: SegmentStatus;
  error?: string | null;
  has_output: boolean;
  output_duration?: number | null;
  clip_id?: string | null;
  updated_at?: string | null;
}

export interface DirectEstimate {
  video_usd: number;
  anchor_usd: number;
  total_usd: number;
  video_usd_per_second: number;
  audio?: boolean;
}

export interface SingleEstimate extends DirectEstimate {
  duration_seconds: number;
  min_duration_seconds: number;
  max_duration_seconds: number;
  durations: number[];
}

export interface DirectProject {
  id: string;
  title: string;
  prompt: string;
  aspect_ratio: AspectRatio;
  workflow_version: string;
  status: DirectStatus;
  error?: string | null;
  references: DirectReference[];
  plan: DirectPlan | null;
  max_clips: number;
  estimate: DirectEstimate;
  duration_seconds?: number | null;
  audio?: boolean;
  complexity?: string | null;
  duration_reason?: string | null;
  credits_required?: number | null;
  credits_charged?: number | null;
  segments: DirectSegment[];
  progress: { ready: number; total: number; current_index: number | null };
  created_at: string;
  updated_at: string;
}

const P = "/direct/projects";

export const createDirectProject = (data: { prompt: string; aspect_ratio: AspectRatio; client_token?: string }) =>
  api.post<DirectProject>(P, data).then((r) => r.data);
export const getDirectProject = (id: string) => api.get<DirectProject>(`${P}/${id}`).then((r) => r.data);
export const addDirectReference = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post<DirectProject>(`${P}/${id}/references`, fd).then((r) => r.data);
};
export const deleteDirectReference = (id: string, refId: string) =>
  api.delete<DirectProject>(`${P}/${id}/references/${refId}`).then((r) => r.data);
export const directReferencePath = (id: string, refId: string) => `/direct/projects/${id}/references/${refId}/image`;
export const analyzeDirectProject = (id: string) => api.post<DirectProject>(`${P}/${id}/analyze`).then((r) => r.data);
export const generateDirectProject = (id: string, confirmedCredits?: number | null) =>
  api.post<DirectProject>(`${P}/${id}/generate`, { confirmed_credits: confirmedCredits ?? null }).then((r) => r.data);
export const retryDirectSegment = (id: string, index: number) =>
  api.post<DirectProject>(`${P}/${id}/segments/${index}/retry`).then((r) => r.data);
export const directSegmentVideoUrl = (segmentId: string, download = false) =>
  `/direct/segments/${segmentId}/video${download ? "?download=true" : ""}`;

export const SINGLE_WORKFLOW = "single_video_v4";
export const isDirectProject = (p: { workflow_version?: string | null }) =>
  p.workflow_version === "direct_video_v3" || p.workflow_version === SINGLE_WORKFLOW;
export const isSingleProject = (p: { workflow_version?: string | null }) => p.workflow_version === SINGLE_WORKFLOW;
export const singleEstimate = (duration: number, references: number) =>
  api.get<SingleEstimate>(`/direct/single/estimate`, { params: { duration, references } }).then((r) => r.data);
export const createSingleProject = (data: { prompt: string; aspect_ratio: AspectRatio; client_token?: string }) =>
  api.post<DirectProject>("/direct/single", data).then((r) => r.data);
// Optimizer (non-paid): disciplined prompts + quality-aware duration + exact cost, before the paid confirmation.
export const prepareSingleProject = (id: string, data: { prompt?: string; aspect_ratio?: AspectRatio }) =>
  api.post<DirectProject>(`/direct/single/${id}/prepare`, data).then((r) => r.data);
export const directProjectPath = (id: string) => `/direct/${id}`;
